import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { TYPE } from 'vue-toastification'
import { toast } from '@/helpers/toast'
import { copyToClipboard } from '@/helpers/copyToClipboard'

import { deleteDrill } from '@/api/drill/delete'
import { duplicateDrill } from '@/api/drill/duplicate'
import { resetDrill } from '@/api/drill/reset'

import { DrillManageActionType } from '@/types'
import type { Drill } from '@acepoker/contract'

export function useDrillManagement() {
  const { t } = useI18n()
  const router = useRouter()

  const deleteConfirmModal = ref()
  const resetConfirmModal = ref()

  const queryClient = useQueryClient()
  const { mutate: deleteDrillMutation } = useMutation({
    mutationFn: deleteDrill,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['custom-drills'] })
      toast({
        message: t('CustomDrills.message.DrillDeleted'),
        icon: TYPE.SUCCESS
      })
    },
    onError(error) {
      showErrorToast(error)
    }
  })
  const { mutate: duplicateDrillMutation } = useMutation({
    mutationFn: duplicateDrill,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['custom-drills'] })
      toast({
        message: t('CustomDrills.message.DrillDuplicated'),
        icon: TYPE.SUCCESS
      })
    },
    onError(error) {
      showErrorToast(error)
    }
  })
  const { mutate: resetDrillMutation } = useMutation({
    mutationFn: resetDrill,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['custom-drills'] })
      toast({
        message: t('CustomDrills.message.DrillReset'),
        icon: TYPE.SUCCESS
      })
    },
    onError(error) {
      showErrorToast(error)
    }
  })

  async function handleManageAction(
    type: DrillManageActionType,
    { id, path }: { id: Drill['id']; path: string },
    callback?: () => void
  ) {
    switch (type) {
      case DrillManageActionType.edit:
        router.push({ path: `/games/${path}/${id}` })
        break
      case DrillManageActionType.delete:
        const { isCanceled: isDeleteCanceled } = await deleteConfirmModal.value.confirm()
        if (!isDeleteCanceled) {
          deleteDrillMutation(id)
          callback?.()
        }
        break
      case DrillManageActionType.duplicate:
        duplicateDrillMutation(id)
        break
      case DrillManageActionType.share:
        copyToClipboard(`${window.location.origin}/drill/${id}`)
        toast({
          message: t('CustomDrills.message.DrillLinkCopied'),
          icon: TYPE.SUCCESS
        })
        break
      case DrillManageActionType.resetStats:
        const { isCanceled: isResetCanceled } = await resetConfirmModal.value.confirm()
        if (!isResetCanceled) resetDrillMutation(id)
        break
    }
  }

  // helper
  function showErrorToast(error: Error) {
    toast({
      message: t('Errors.label.Oops'),
      details: error.message,
      icon: TYPE.ERROR
    })
  }

  return {
    deleteConfirmModal,
    resetConfirmModal,

    handleManageAction
  }
}
